'use client';

import { c as _c } from "react/compiler-runtime";
import type { ApparelBundleRung } from '@/lib/apparel-bundle-config';
import { getRungUnitPrice } from '@/lib/apparel-bundle-config';
import { formatMoneyAmount } from '@/lib/money';
import type { ApparelBundlePick } from './apparel-bundle-math';
import type { ApparelBundleState } from './use-apparel-bundle-state';
interface ApparelBundleStripProps {
  state: ApparelBundleState;
  moneyLocale?: string;
}

/**
 * How far along the ladder the cart is, as a fraction of its width. Each
 * column's right edge is a stop (1 item, then each rung's item count), and
 * the fill interpolates between stops — two items with a Buy 3 column sit
 * halfway across it, which is the whole point: progress has to be visible
 * before a rung is reached, not only after.
 */
function getProgress(state: ApparelBundleState): number {
  const stops = [1, ...state.config.rungs.map(rung => rung.items)];
  const count = state.pricing.count;
  if (count <= 0) return 0;
  const width = 1 / stops.length;
  let previous = 0;
  for (let index = 0; index < stops.length; index++) {
    const stop = stops[index] ?? 0;
    if (count >= stop) {
      previous = stop;
      continue;
    }
    return width * index + width * ((count - previous) / (stop - previous));
  }
  return 1;
}

/**
 * "Most popular" goes on the three-item rung when the ladder has one — the
 * middle of the ladder otherwise. A tag, not a bigger column.
 */
function getFeaturedRung(rungs: ApparelBundleRung[]): ApparelBundleRung | null {
  return rungs.find(rung => rung.items === 3) ?? rungs[Math.floor((rungs.length - 1) / 2)] ?? null;
}
interface LadderColumn {
  key: string;
  label: string;
  /** Headline: the sale percent on Buy 1 ("15% off"), the rung percent after ("25% off"). */
  headline: string;
  /** MSRP, struck through beside the price; null when the product has none. */
  listPrice: number | null;
  /** What one of THIS product costs at the rung. */
  unitPrice: number;
  tag: string | null;
  /** The rung the bundle currently sits on. */
  active: boolean;
  /** The rung we'd like people to reach — set a little larger. */
  featured: boolean;
}

/**
 * One column per step of the ladder, priced for the product on this page:
 * "Buy 3 · 25% off · ~~$39.99~~ $29.99 each". The percent leads (it's the
 * promise, and the same on every product); the price line explains it.
 * Per-item figures are the only ones that stay true when the slots hold a
 * mix of tees and hoodies — the real total saving for what's actually in the
 * cart sits in the status line below.
 */
function buildColumns(state: ApparelBundleState, current: ApparelBundlePick | null): LadderColumn[] {
  const {
    config,
    pricing
  } = state;
  const price = current?.price ?? 0;
  const compareAt = current?.compareAt ?? null;
  const listPrice = compareAt && compareAt > price ? compareAt : null;
  const salePercent = listPrice ? Math.round((1 - price / listPrice) * 100) : 0;
  const featured = getFeaturedRung(config.rungs);
  return [{
    key: 'one',
    label: 'Buy 1',
    headline: salePercent > 0 ? `${salePercent}% off` : "Today's price",
    listPrice,
    unitPrice: price,
    tag: null,
    active: !pricing.rung,
    featured: false
  }, ...config.rungs.map(rung => ({
    key: `rung-${rung.items}`,
    label: `Buy ${rung.items}`,
    headline: `${rung.percentOff}% off`,
    listPrice: listPrice ?? (price > 0 ? price : null),
    unitPrice: getRungUnitPrice(price, compareAt, rung.percentOff),
    tag: rung === featured ? 'Most popular' : null,
    active: pricing.rung?.items === rung.items,
    featured: rung === featured
  }))];
}
function LadderColumnItem(t0) {
  const $ = _c(30);
  const {
    column,
    first,
    currencyCode,
    moneyLocale
  } = t0;
  const t1 = column.active ? "step" : undefined;
  const t2 = `pt-3 ${first ? "" : "border-l border-neutral-200 pl-3 pr-1"}`;
  const t3 = `block text-[11px] font-semibold uppercase tracking-[0.08em] ${column.active || column.featured ? "text-neutral-900" : "text-neutral-500"}`;
  let t4;
  if ($[0] !== column.label || $[1] !== t3) {
    t4 = <span className={t3}>{column.label}</span>;
    $[0] = column.label;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = `mt-1.5 block font-bold leading-none ${column.featured ? "text-[21px]" : "text-[17px]"} ${column.active && !first ? "text-brand-accent" : "text-neutral-900"}`;
  let t6;
  if ($[3] !== column.headline || $[4] !== t5) {
    t6 = <span className={t5}>{column.headline}</span>;
    $[3] = column.headline;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== column.listPrice || $[7] !== column.unitPrice || $[8] !== currencyCode || $[9] !== moneyLocale) {
    t7 = column.listPrice != null && column.listPrice > column.unitPrice && <span className="text-neutral-500 line-through">{formatMoneyAmount(column.listPrice, currencyCode, moneyLocale)}</span>;
    $[6] = column.listPrice;
    $[7] = column.unitPrice;
    $[8] = currencyCode;
    $[9] = moneyLocale;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== column.unitPrice || $[12] !== currencyCode || $[13] !== moneyLocale) {
    t8 = formatMoneyAmount(column.unitPrice, currencyCode, moneyLocale);
    $[11] = column.unitPrice;
    $[12] = currencyCode;
    $[13] = moneyLocale;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <span className="font-semibold">{t8}</span>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-[11px] text-neutral-500">each</span>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t7 || $[19] !== t9) {
    t11 = <span className="mt-1.5 flex flex-wrap items-baseline gap-x-1 text-[12px] leading-tight tabular-nums text-neutral-900">{t7}{t9}{t10}</span>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== column.tag) {
    t12 = column.tag && <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-accent">{column.tag}</span>;
    $[21] = column.tag;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t1 || $[24] !== t11 || $[25] !== t12 || $[26] !== t2 || $[27] !== t4 || $[28] !== t6) {
    t13 = <li aria-current={t1} className={t2}>{t4}{t6}{t11}{t12}</li>;
    $[23] = t1;
    $[24] = t11;
    $[25] = t12;
    $[26] = t2;
    $[27] = t4;
    $[28] = t6;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}

/**
 * The bundle ladder as a quiet strip under the add-to-cart button: a hairline
 * ladder (one column per step, priced per item for this product) and a row of
 * small slots showing what's counted — the eligible items already in the cart,
 * nothing else — with the real saving beside them. Nothing to operate: the
 * cart is the bundle, and adding this product is what moves the ladder.
 */
export function ApparelBundleStrip(t0) {
  const $ = _c(38);
  const {
    state,
    moneyLocale
  } = t0;
  const {
    config,
    pricing
  } = state;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== config.heading || $[1] !== config.subheading || $[2] !== moneyLocale || $[3] !== pricing || $[4] !== state) {
    const columns = buildColumns(state, state.current);
    const progress = getProgress(state);
    t5 = "border-y border-neutral-200 py-4";
    let t8;
    if ($[12] !== config.heading) {
      t8 = <span className="block text-[14px] font-bold uppercase tracking-wide text-neutral-900">{config.heading}</span>;
      $[12] = config.heading;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== config.subheading) {
      t9 = config.subheading && <p className="mt-0.5 text-[13px] leading-snug text-neutral-700">{config.subheading}</p>;
      $[14] = config.subheading;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    if ($[16] !== t8 || $[17] !== t9) {
      t6 = <div>{t8}{t9}</div>;
      $[16] = t8;
      $[17] = t9;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t10;
    if ($[19] !== progress) {
      t10 = Math.round(progress * 100);
      $[19] = progress;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    const t11 = `${progress * 100}%`;
    let t12;
    if ($[21] !== t11) {
      t12 = <div className="absolute inset-y-0 left-0 bg-neutral-900 transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none" style={{
        width: t11
      }} />;
      $[21] = t11;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    t7 = <div role="progressbar" aria-label="Bundle progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={t10} className="relative mt-3 h-[3px] bg-neutral-200">{t12}{columns.slice(0, -1).map((column, index) => <span key={column.key} aria-hidden="true" className="absolute -bottom-[3px] top-0 w-px bg-neutral-400" style={{
        left: `${(index + 1) / columns.length * 100}%`
      }} />)}</div>;
    t1 = "Bundle pricing";
    t2 = "grid";
    const t13 = `repeat(${columns.length}, minmax(0, 1fr))`;
    if ($[23] !== t13) {
      t3 = {
        gridTemplateColumns: t13
      };
      $[23] = t13;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    let t14;
    if ($[25] !== moneyLocale || $[26] !== pricing) {
      t14 = (column_0, index_0) => <LadderColumnItem key={column_0.key} column={column_0} first={index_0 === 0} currencyCode={pricing.currencyCode} moneyLocale={moneyLocale} />;
      $[25] = moneyLocale;
      $[26] = pricing;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    t4 = columns.map(t14);
    $[0] = config.heading;
    $[1] = config.subheading;
    $[2] = moneyLocale;
    $[3] = pricing;
    $[4] = state;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[28] !== t1 || $[29] !== t2 || $[30] !== t3 || $[31] !== t4) {
    t8 = <ol aria-label={t1} className={t2} style={t3}>{t4}</ol>;
    $[28] = t1;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== t5 || $[34] !== t6 || $[35] !== t7 || $[36] !== t8) {
    t9 = <div className={t5}>{t6}{t7}{t8}</div>;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
}